import { Button } from "@/design-system/Button/Button";
import { MyClubSubsection } from "@/events/myClubMixpanel";
import { useTranslation } from "@repo/shared/i18n";
import { useLocation } from "react-router";

import { useStartClubImport } from "./EmptyClub/useStartClubImport";

/**
 * The tab the button was pressed on. It is what `import_club` has always
 * reported for this button, so it stays the origin rather than the "Toolbar"
 * the button sits in — renaming it would split the existing series.
 *
 * Safe for the signup context, which reads the same origin: this button only
 * renders once a club exists, so its reader (a first-time login) never runs.
 */
const myClubSubsectionFromPath = (pathname: string): MyClubSubsection => {
  if (pathname.startsWith("/my-club/players")) return "My Players";
  if (pathname.startsWith("/my-club/sbc-fodder-breakdown"))
    return "SBC Fodder Breakdown";
  return "Overview";
};

interface StartImportProps {
  disabled: boolean;
  /** The club home shows it quietly, as "Re-import", next to the page title. */
  variant?: "primary" | "secondary" | "tertiary";
  size?: "small" | "medium";
}

/**
 * My Club's Import button. It owns no import logic of its own any more — the
 * decision (launch here, or route to the tutorial) and the overlay both live in
 * useStartClubImport, so this button behaves exactly like every other club
 * import CTA in the app.
 */
export const StartImport = ({
  disabled,
  variant = "primary",
  size = "small",
}: StartImportProps) => {
  const { t } = useTranslation(["clubImport", "common", "myClub"]);
  const location = useLocation();
  const startClubImport = useStartClubImport({
    section: "My Club",
    subsection: myClubSubsectionFromPath(location.pathname),
  });

  // Both events (the click, and the import if one starts) are fired by
  // useStartClubImport, so this only has to say where it sits.
  const handleImportStart = () => {
    if (!disabled) startClubImport();
  };

  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={(e) => {
        handleImportStart();
        e.stopPropagation();
      }}
    >
      {variant !== "primary" ? (
        <span className="whitespace-nowrap">
          {t("myClub:nav.reimportShort")}
        </span>
      ) : (
        <span className="flex flex-row gap-x-1 whitespace-nowrap">
          <span>{t("common:import")}</span>
          <span className="hidden mobile:block">{t("startImport.club")}</span>
        </span>
      )}
    </Button>
  );
};
